/**
 * @Author: Klaudie <klaudie@foxprofi.cz>
 */

import { Menu, ChevronsLeft, Home, Users, Settings, BarChart, Package, Building2, ShieldCheck, Layers, Upload, Receipt } from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { usePermissions } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import type { LucideIcon } from 'lucide-react'

// Mapování slug modulů na ikony
const moduleIcons: Record<string, LucideIcon> = {
  admin: Users,
  reporting: BarChart,
  settings: Settings,
}

// Mapování slug funkcí na ikony
const functionIcons: Record<string, LucideIcon> = {
  manage_modules: Package,
  manage_organizations: Building2,
  manage_roles: ShieldCheck,
  manage_users: Users,
  upload_documents: Upload,
  incoming_invoices: Receipt,
}

// Mapování slug funkcí na cesty
const functionRoutes: Record<string, string> = {
  manage_users: '/admin/users',
  manage_roles: '/admin/roles',
  view_reports: '/reports',
  upload_documents: '/invoices/upload-documents',
  incoming_invoices: '/invoices/incoming-invoices',
}

export function Sidebar() {
  const location = useLocation()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()
  const { modules } = usePermissions()

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 h-screen bg-white border-r border-border transition-all duration-300 z-40',
        sidebarCollapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Header s toggle buttonem */}
      <div className="h-16 flex items-center justify-between pl-4 pr-2 border-b border-border">
        {!sidebarCollapsed && (
          <Link to="/dashboard" className="flex flex-col">
            <span className="text-xl font-bold text-primary">MENYXO</span>
            <span className="text-xs text-muted-foreground -mt-1">Manage Your Matrix</span>
          </Link>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className={cn(sidebarCollapsed && 'mx-auto')}
        >
          {sidebarCollapsed ? <Menu size={20} /> : <ChevronsLeft size={20} />}
        </Button>
      </div>

      {/* Navigace */}
      <nav className="p-2 space-y-1">
        {/* Dashboard - vždy viditelný */}
        <Link
          to="/dashboard"
          className={cn(
            'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
            location.pathname === '/dashboard'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            sidebarCollapsed && 'justify-center'
          )}
          title={sidebarCollapsed ? 'Dashboard' : undefined}
        >
          <Home size={20} />
          {!sidebarCollapsed && <span>Dashboard</span>}
        </Link>

        {/* Dynamické menu podle modulů */}
        {modules && modules.length > 0 && modules.map((module) => {
          const Icon = moduleIcons[module.slug] || Layers

          return (
            <div key={module.id} className="space-y-1">
              {/* Hlavička modulu (pokud má více funkcí) */}
              {!sidebarCollapsed && (
                <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                  {module.name}
                </div>
              )}

              {/* Funkce modulu */}
              {module.functions.map((func) => {
                const route = functionRoutes[func.slug] || `/${module.slug}/${func.slug}`
                const isActive = location.pathname.startsWith(route)
                const FunctionIcon = functionIcons[func.slug] || Icon

                return (
                  <Link
                    key={func.id}
                    to={route}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      sidebarCollapsed && 'justify-center'
                    )}
                    title={sidebarCollapsed ? func.name : undefined}
                  >
                    <FunctionIcon size={20} />
                    {!sidebarCollapsed && <span>{func.name}</span>}
                  </Link>
                )
              })}
            </div>
          )
        })}
      </nav>
    </aside>
  )
}
